import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaArrowRight, FaCheck, FaUserTie, FaFileAlt, FaDollarSign } from 'react-icons/fa';
import {useNavigate} from 'react-router-dom'; 

const ReviewWorkflowDemo = () => {
  const [activeStep, setActiveStep] = useState(0);
  const navigate = useNavigate();

  const handleStartReviewProcess = () => {
    navigate("/signin");
  }

  const steps = [
    {
      title: "Generate Your Dataset",
      description: "Create your synthetic dataset using our powerful generation tools",
      icon: <FaFileAlt className="h-6 w-6" />
    },
    {
      title: "Request Expert Review",
      description: "Click the review button to get your dataset evaluated by industry experts",
      icon: <FaUserTie className="h-6 w-6" />
    },
    {
      title: "Receive Quote & Match",
      description: "We'll provide a pricing quote and match you with the perfect expert for your needs",
      icon: <FaDollarSign className="h-6 w-6" />
    },
    {
      title: "Get Reviewed Dataset",
      description: "Receive your expert-reviewed dataset with human insights and improvements",
      icon: <FaCheck className="h-6 w-6" />
    }
  ];

  return (
    <div className="py-24 bg-gradient-to-br from-white to-blue-50 relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -left-40 w-96 h-96 bg-blue-100 rounded-full opacity-10 blur-3xl"></div>
        <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-indigo-100 rounded-full opacity-10 blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-bold text-blue-600 mb-4">
            Expert Review Workflow
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Get your synthetic data reviewed by industry experts for maximum quality and reliability
          </p>
        </motion.div>

        <div className="max-w-4xl mx-auto">
          {/* Timeline */}
          <div className="relative">
            {/* Progress line */}
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gray-200"></div>
            <div 
              className="absolute left-8 top-0 w-0.5 bg-gradient-to-b from-blue-500 to-green-500 transition-all duration-500"
              style={{ height: `${(activeStep / (steps.length - 1)) * 100}%` }}
            ></div>

            {/* Steps */}
            <div className="space-y-12">
              {steps.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ 
                    duration: 0.6,
                    delay: index * 0.1
                  }}
                  className="relative pl-16 group cursor-pointer"
                  onClick={() => setActiveStep(index)}
                >
                  {/* Step indicator */}
                  <div className={`absolute left-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
                    index <= activeStep 
                      ? 'bg-gradient-to-r from-blue-500 to-green-500 text-white' 
                      : 'bg-gray-200 text-gray-400'
                  }`}>
                    {step.icon}
                  </div>

                  {/* Content */}
                  <div className={`transition-all duration-300 ${
                    index === activeStep ? 'scale-105' : 'opacity-50'
                  }`}>
                    <h3 className="text-xl font-semibold text-blue-600 mb-2">{step.title}</h3>
                    <p className="text-gray-600">{step.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* CTA Button */}
          <div className="text-center mt-12">
            <button onClick={handleStartReviewProcess} className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-8 py-3 rounded-full hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl">
              Start Your Review Process
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewWorkflowDemo; 